
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { useEffect, useRef, useState } from 'react';
import Seo from '@/components/Seo';
import Turnstile, { isTurnstileEnabled, TURNSTILE_ERROR_MESSAGE, type TurnstileHandle, } from '@/components/Turnstile';
import { createClient } from '@/utils/supabase/component';
// Landing page for failed OTP verification (~70 users/day). The dominant
// cause is a consumed/expired one-time token: the user clicked the email link
// twice, an inbox link-scanner burned it, or the link was old. In most of
// those cases the account is fine (often already verified) — so instead of a
// scary dead-end suggesting "sign up again", this page:
//   1. auto-redirects to the dashboard when a session already exists
//   2. leads with "sign in" (the account exists!)
//   3. offers a self-serve fresh-link resend
export default function AuthCodeError() {
    const router = useRouter();
    const { t } = useLocale('login');
    const supabase = createClient();
    // The confirm route forwards the failed OTP's type; recovery links need a
    // password-reset email, not a signup-confirmation resend.
    const isRecovery = router.query.type === 'recovery';
    const [checkingSession, setCheckingSession] = useState(true);
    const [email, setEmail] = useState('');
    const [resending, setResending] = useState(false);
    // Resend / password recovery are captcha-protected once Supabase Attack
    // Protection is on.
    const captchaRef = useRef<TurnstileHandle>(null);
    const [resendResult, setResendResult] = useState<{
        type: 'success' | 'info' | 'error';
        text: string;
    } | null>(null);
    // If the first click already verified + signed the user in, this second
    // visit shouldn't show an error at all.
    useEffect(() => {
        let cancelled = false;
        if (!router.isReady)
            return;
        if (isRecovery) {
            // A recovery visitor wants a fresh reset link, not the dashboard.
            setCheckingSession(false);
            return;
        }
        supabase.auth
            .getUser()
            .then(({ data }) => {
            if (cancelled)
                return;
            if (data.user) {
                router.replace('/dashboard');
            }
            else {
                setCheckingSession(false);
            }
        })
            .catch(() => {
            if (!cancelled)
                setCheckingSession(false);
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.isReady, isRecovery]);
    const handleResend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim() || resending)
            return;
        setResending(true);
        setResendResult(null);
        try {
            const captchaToken = await captchaRef.current?.getToken();
            if (isTurnstileEnabled && !captchaToken) {
                setResendResult({ type: 'error', text: t(TURNSTILE_ERROR_MESSAGE) });
                return;
            }
            const { error } = isRecovery
                ? await supabase.auth.resetPasswordForEmail(email.trim(), {
                    redirectTo: `${window.location.origin}/auth/confirm?type=recovery&next=/auth/reset-password`,
                    captchaToken,
                })
                : await supabase.auth.resend({
                    type: 'signup',
                    email: email.trim(),
                    options: {
                        emailRedirectTo: `${window.location.origin}/auth/confirm`,
                        captchaToken,
                    },
                });
            if (!error) {
                setResendResult({
                    type: 'success',
                    text: t(isRecovery
                        ? 'A fresh password reset link is on its way — check your inbox.'
                        : 'A fresh confirmation link is on its way — check your inbox.'),
                });
            }
            else if (/already confirmed|already been confirmed/i.test(error.message)) {
                // Best outcome: nothing was wrong — the earlier click verified them.
                setResendResult({
                    type: 'info',
                    text: t('Good news — this email is already verified. Just sign in.'),
                });
            }
            else {
                setResendResult({ type: 'error', text: error.message });
            }
        }
        catch {
            setResendResult({
                type: 'error',
                text: t('Could not resend the email. Please try again.'),
            });
        }
        finally {
            // Turnstile tokens are single use — clear it whatever the outcome.
            captchaRef.current?.reset();
            setResending(false);
        }
    };
    if (checkingSession) {
        return (<div className='flex min-h-screen items-center justify-center dark:bg-slate-900'>
        <Seo templateTitle='Verifying'/>
        <div className='h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600'/>
      </div>);
    }
    return (<div className='flex min-h-screen flex-col items-center justify-center py-12 dark:bg-slate-900'>
      <Seo templateTitle='Link Expired'/>
      <div className='w-full max-w-md px-4'>
        <div>
          <Link href='/' className='' passHref legacyBehavior>
            <span className='flex justify-center text-2xl font-extrabold text-primary-700'>
              <Image src='/images/marky.png' width='250' height='36' alt='Easy-Peasy.ai logo'/>
            </span>
          </Link>
          <h2 className='mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-slate-200'>
            {t('This link has expired or was already used')}
          </h2>
          <p className='mt-3 text-center text-sm text-gray-600 dark:text-slate-400'>
            {t(isRecovery
            ? 'No worries — password reset links are single-use and expire quickly. Request a fresh one below.'
            : 'No worries — your account is safe. If you already clicked the link once, your email is likely verified and you can simply sign in.')}
          </p>
        </div>

        <div className='mt-8 space-y-4'>
          <Link href='/login' className='flex w-full justify-center rounded-md border border-transparent bg-primary-600 py-2.5 px-4 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'>
            {t('Sign in')}
          </Link>

          <div className='relative py-2'>
            <div className='absolute inset-0 flex items-center'>
              <div className='w-full border-t border-gray-200 dark:border-gray-700'/>
            </div>
            <div className='relative flex justify-center text-xs'>
              <span className='bg-white px-2 text-gray-500 dark:bg-slate-900 dark:text-gray-400'>
                {t('or get a fresh link')}
              </span>
            </div>
          </div>

          <form onSubmit={handleResend} className='space-y-3'>
            <input type='email' required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('Your email address')} className='block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm'/>
            <Turnstile ref={captchaRef} action={isRecovery ? 'password-reset' : 'resend'} className='flex justify-center'/>
            <button type='submit' disabled={resending} className='flex w-full justify-center rounded-md border border-gray-300 bg-white py-2.5 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'>
              {resending
            ? t('Sending...')
            : t(isRecovery
                ? 'Email me a new password reset link'
                : 'Email me a new confirmation link')}
            </button>
          </form>

          {resendResult && (<div className={`rounded-md p-3 text-sm ${resendResult.type === 'error'
                ? 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                : resendResult.type === 'info'
                    ? 'bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                    : 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400'}`}>
              {resendResult.text}
            </div>)}

          <Link href='/auth/forgot-password' className='flex w-full justify-center text-sm text-gray-600 hover:text-gray-500 dark:text-gray-400'>
            {t('Reset password')}
          </Link>
        </div>
      </div>
    </div>);
}

    async function __Next_Translate__getStaticProps__1a11b03d460__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/auth/auth-code-error',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a11b03d460__ as getStaticProps }
  